import { useState, useEffect, useCallback } from "react";
import { Button, Input, Select, DatePicker, Space, message, Modal, Tag } from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import usePermission from "../../hooks/usePermission";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import {
  listBroadcasts,
  deleteBroadcast,
  exportBroadcasts,
  downloadBroadcastAttachment,
} from "../../services/broadcast.service";

const { RangePicker } = DatePicker;

const TARGET_FILTER_OPTIONS = [
  { label: "All Riders", value: "all" },
  { label: "By Project", value: "project" },
  { label: "By Zone", value: "zone" },
  { label: "By City", value: "city" },
  { label: "Select Individuals", value: "individuals" },
];

// "sending" is transient — a broadcast currently being dispatched. Anything
// unrecognised falls through to red so a failure is never shown as success.
const STATUS_COLORS = {
  sent: "green",
  scheduled: "gold",
  sending: "blue",
  failed: "red",
};

function formatDateTime(value) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function BroadcastHistoryTab({ refreshKey }) {
  const { canDelete } = usePermission("communications");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [targetType, setTargetType] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search || undefined,
        targetType: targetType || undefined,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].startOf("day").toISOString();
        params.toDate = dateRange[1].endOf("day").toISOString();
      }

      const response = await listBroadcasts(params);
      setRows(response?.data?.records || []);
      setTotalCount(response?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load broadcast history");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, targetType, dateRange]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Broadcast",
      content: `Remove "${record.title}" from broadcast history? This does not un-send messages already delivered.`,
      onConfirm: async () => {
        try {
          await deleteBroadcast(record._id);
          message.success("Broadcast deleted");
          fetchList();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Delete failed");
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const raw = await exportBroadcasts({
        search: search || undefined,
        targetType: targetType || undefined,
      });
      const blob = raw instanceof Blob ? raw : new Blob([raw], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `Broadcast_History_${dayjs().format("YYYY-MM-DD")}.csv`);
      message.success("Export downloaded");
    } catch {
      message.error("Export failed");
    }
  };

  const handleDownloadAttachment = async (record) => {
    try {
      const blob = await downloadBroadcastAttachment(record._id);
      downloadBlob(blob, record.attachmentFileName || "broadcast-attachment");
    } catch {
      message.error("Failed to download attachment");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (v, record) => (
        <span>
          <strong>{v}</strong>
          {record.priority === "urgent" && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Urgent
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Target Audience",
      dataIndex: "targetAudienceLabel",
      key: "targetAudienceLabel",
      width: 150,
    },
    {
      title: "Sent By",
      dataIndex: "sentByName",
      key: "sentByName",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "red"}>{status}</Tag>
      ),
    },
    {
      title: "Sent Date/Time",
      key: "sentDateTime",
      width: 160,
      render: (_, record) => formatDateTime(record.sentAt || record.scheduledAt),
    },
    {
      title: "Delivery Count",
      dataIndex: "deliveryCount",
      key: "deliveryCount",
      width: 120,
      align: "center",
    },
    {
      title: "Action",
      key: "actions",
      fixed: "right",
      width: canDelete ? 110 : 60,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewRecord(record)}
          />
          {canDelete && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          background: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: 10,
          padding: 16,
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 220px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Search Title
          </div>
          <Input.Search
            placeholder="Broadcast title"
            allowClear
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
        </div>

        <div style={{ width: 180 }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Target Audience
          </div>
          <Select
            allowClear
            placeholder="All Audiences"
            style={{ width: "100%" }}
            options={TARGET_FILTER_OPTIONS}
            value={targetType}
            onChange={(v) => {
              setPage(1);
              setTargetType(v);
            }}
          />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Date Range
          </div>
          <RangePicker
            value={dateRange}
            onChange={(v) => {
              setPage(1);
              setDateRange(v);
            }}
          />
        </div>

        <Button icon={<ReloadOutlined />} onClick={fetchList}>
          Refresh
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={viewRecord?.title}
        open={!!viewRecord}
        onCancel={() => setViewRecord(null)}
        footer={
          viewRecord?.hasAttachment ? (
            <Button icon={<PaperClipOutlined />} onClick={() => handleDownloadAttachment(viewRecord)}>
              Download Attachment ({viewRecord.attachmentFileName})
            </Button>
          ) : null
        }
      >
        {viewRecord && (
          <div>
            <p style={{ whiteSpace: "pre-wrap", marginBottom: 16 }}>{viewRecord.body}</p>
            <Space direction="vertical" size={4} style={{ fontSize: 12, color: "#6b7280" }}>
              <span>Target Audience: {viewRecord.targetAudienceLabel}</span>
              <span>Sent By: {viewRecord.sentByName || "—"}</span>
              <span>Sent: {formatDateTime(viewRecord.sentAt || viewRecord.scheduledAt)}</span>
              <span>Delivery Count: {viewRecord.deliveryCount}</span>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
}
